import { useState, useEffect } from 'react';
import jsonToFormData from 'json-form-data';
import { useTranslation } from 'react-i18next';

import i18n from '../../../app/i18n';
import { api } from '../../../lib/api';
import { Fetcher, htmlSanitizer } from '../../../lib/helpers';
import { QuizHeader, Divider } from '../StyledComponents';
import TileButtonComponent from './TileButtonComponent.jsx';
import { Button } from '../../../shared/FormStyles/Button';

const validateEmail = value => {
  const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; // eslint-disable-line
  return re.test(String(value).toLowerCase());
};

const validatePhone = value => {
  // eslint-disable-next-line no-useless-escape
  const re = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im;
  return re.test(String(value));
};

const decideIfAnswerIsCorrect = (
  questionType,
  correctIndex,
  index,
  freeForm,
) => {
  if (freeForm) {
    return true;
  } else if (
    !freeForm &&
    (questionType === 'survey' || correctIndex === -1 || correctIndex === index)
  ) {
    return true;
  }
  return false;
};

const determineIfMarkedCorrect = (tile, key) =>
  (tile.answerIndex === key && tile.complete) ||
  (tile.origin === 'complete' &&
    tile.correctAnswerIndex === key &&
    !tile.complete)
    ? 'clicked_right_answer'
    : '';

const sharedPlanTile = tile => tile.status === 'plan' && tile.isSharable;
const tileNotSubmittable = tile => {
  if (tile.origin === 'campaign') return false;

  return tile.complete || tile.origin === 'complete' || tile.status === 'plan';
};

const updateCharCount = (e, limit, setCharCount) => {
  const charsLeft = limit - e.target.value.length;

  setCharCount(charsLeft);
};

const checkAnswerForSubmission = (correctIndex, index, tile) => {
  const {
    id,
    submitAnswer,
    questionType,
    questionSubtype,
    allowFreeResponse,
    answers,
  } = tile;
  const freeForm =
    questionSubtype === 'free_response' || (allowFreeResponse && index === -1)
      ? document.getElementById('free_form_response')
      : undefined;
  const correctAnswer = decideIfAnswerIsCorrect(
    questionType,
    correctIndex,
    index,
    freeForm,
  );
  if (tileNotSubmittable(tile)) {
    return;
  }

  const freeFormAnswer = freeForm ? freeForm.value : null;

  const indexAnswer = freeForm
    ? allowFreeResponse
      ? answers.length
      : null
    : index;
  submitAnswer(id, indexAnswer, freeFormAnswer, correctAnswer);
};

const FreeResponse = ({
  style,
  showXBtn,
  tile,
  submitAction,
  submitBtnText,
}) => {
  const { t } = useTranslation('tileView');
  const [charCount, setCharCount] = useState(400);

  return (
    <div
      className="free-text-panel content_sections"
      id="free-response-textarea"
      style={style || {}}
    >
      {showXBtn && (
        <i
          className="free-text-hide fa-solid fa-xmark fa-1x"
          onClick={e => {
            e.target.parentElement.style.display = 'none';
            if (document.getElementById('free-response-button')) {
              document.getElementById('free-response-button').style.display =
                'block';
            }
          }}
        />
      )}
      <textarea
        name="free_form_response"
        id="free_form_response"
        maxLength="400"
        placeholder="Enter your response here"
        className="free-form-response edit with_counter"
        readOnly={tile.origin === 'complete' || tile.freeFormResponse}
        onKeyUp={e => updateCharCount(e, 400, setCharCount)}
        value={
          tile.origin === 'complete' || tile.freeFormResponse
            ? tile.freeFormResponse || ''
            : undefined
        }
      />
      <div className="character-counter">
        {t('tileQuiz.characterCount', { charCount })}
      </div>
      <TileButtonComponent
        key={`${tile.id}-0`}
        answer={submitBtnText || t('tileQuiz.submit')}
        correctAnswer={true}
        points={tile.points}
        complete={!!tile.complete}
        onClick={submitAction}
      />
    </div>
  );
};

const formActions = (e, subtype) => {
  const options = e.target.parentElement.parentElement.children;

  for (let i = 0; i < options.length; i++) {
    options[i].style.display = 'none';
  }
  document.getElementById(`${subtype}_form_hidden`).style.display = '';
};

const revertSelection = e => {
  const options = e.target.parentElement.parentElement.parentElement.children;
  for (let i = 0; i < options.length; i++) {
    options[i].style.display = '';
  }

  e.target.parentElement.parentElement.style.display = 'none';
};

const FreeResponseOption = ({ tileId, markCorrect }) => {
  return (
    <div style={{ display: 'block' }} id="free-response-button" key={tileId}>
      <Button
        variant="answerBlue"
        className={`multiple-choice-answer ${markCorrect}`}
        onClick={e => {
          const freeResponseTextarea = document.getElementById(
            'free-response-textarea',
          );

          e.target.parentElement.style.display = 'none';
          freeResponseTextarea.style.display = 'block';
        }}
        style={{ margin: '0.5em auto' }}
      >
        Other
      </Button>
    </div>
  );
};

const MultipleChoiceAnswers = ({ tile, subtype }) => {
  const [tileAnswers, setTileAnswers] = useState(
    tile.answers.map(answer => ({ answer, disabled: false })),
  );

  useEffect(() => {
    setTileAnswers(tile.answers.map(answer => ({ answer, disabled: false })));
  }, [tile]);

  const disableAll = () =>
    setTileAnswers(tile.answers.map(answer => ({ answer, disabled: true })));

  const answerCollection = tileAnswers.map(
    ({ answer, disabled }, answerIndex) => (
      <div key={answerIndex}>
        <TileButtonComponent
          key={`${tile.id}-${answerIndex}`}
          answer={answer}
          answerIndex={answerIndex}
          tileAnswerIndex={tile.answerIndex}
          isDisabled={disabled}
          correctAnswer={
            subtype === ''
              ? true
              : decideIfAnswerIsCorrect(
                  tile.questionType,
                  tile.correctAnswerIndex,
                  answerIndex,
                  tile.freeForm,
                )
          }
          points={tile.points}
          complete={
            tile.origin === 'complete'
              ? true
              : !!tile.complete && tile.answerIndex === answerIndex
          }
          questionType={tile.questionType}
          subtype={subtype}
          onClick={(e, correctAnswer) => {
            if (
              subtype === 'change_email' ||
              subtype === 'invite_spouse' ||
              subtype === ''
            ) {
              if (answerIndex > 0) {
                if (correctAnswer) {
                  disableAll();
                }
                checkAnswerForSubmission(answerIndex, answerIndex, tile);
              } else {
                formActions(e, subtype === '' ? tile.questionType : subtype);
              }
            } else {
              if (correctAnswer) {
                disableAll();
              }
              checkAnswerForSubmission(
                tile.correctAnswerIndex,
                answerIndex,
                tile,
              );
            }
          }}
          style={{ margin: '0.5em auto' }}
        />
      </div>
    ),
  );
  if (tile.allowFreeResponse) {
    const freeResponseMarkedCorrect = determineIfMarkedCorrect(
      tile,
      tile.answers.length,
    );
    answerCollection.push(
      <FreeResponseOption
        tileId={tile.id}
        markCorrect={freeResponseMarkedCorrect}
      />,
    );
  }
  return answerCollection;
};

const submitEmailChange = (e, tile) => {
  const email = document.getElementById('change_email_email').value;
  const valid = validateEmail(email);
  const { target } = e;
  target.style.pointerEvents = 'none';
  if (email && valid) {
    if (tileNotSubmittable(tile)) {
      target.classList.add('clicked_right_answer');

      return;
    }

    Fetcher.xmlHttpRequest({
      method: 'PUT',
      path: '/change_email',
      params: { change_email: { email } },
      success: resp => {
        if (resp.status === 'success') {
          target.innerText = 'Sending validation to new email address';
          target.classList.add('clicked_right_answer');
          tile.submitAnswer(tile.id, 0, null);
        } else {
          document.getElementById('change_email_email').classList.add('error');
          document.getElementById('email_error').innerText = resp.message;
          target.style.pointerEvents = '';
        }
      },
    });
  } else {
    document.getElementById('change_email_email').classList.add('error');
    document.getElementById('email_error').innerText =
      'A valid email address is required';
  }
};

const renderQuestionAnswerError = (isValidQuestion, isValidContact) => {
  const questionError = i18n.t('tiles:userView.qaQuestionError');
  const contactInfoError = i18n.t('tiles:userView.qaContactInfoError');

  if (!isValidContact) {
    document.getElementById('contact_info_error').innerText = contactInfoError;
    document
      .getElementById('question_answer_contact_info')
      .classList.add('error');
  }

  if (!isValidQuestion) {
    document.getElementById('qa_char_count').classList.add('hide');
    document.getElementById('question_error').classList.remove('hide');
    document.getElementById('question_error').innerText = questionError;
    document.getElementById('question_answer_question').classList.add('error');
  }
};

const clearQuestionAnswerErrors = () => {
  document.getElementById('qa_char_count').classList.remove('hide');
  document.getElementById('question_error').innerText = '';
  document.getElementById('question_error').classList.add('hide');
  document.getElementById('contact_info_error').innerText = '';
  document
    .getElementById('question_answer_contact_info')
    .classList.remove('error');
  document.getElementById('question_answer_question').classList.remove('error');
};

const questionAnswerValue = tile => {
  if (tile.freeFormResponse) return tile.freeFormResponse;

  if (tile.origin === 'complete') return '';

  return undefined;
};

const submitQuestionAnswer = (e, tile) => {
  clearQuestionAnswerErrors();

  const question = document.getElementById('question_answer_question').value;
  const contactInfo = document.getElementById('question_answer_contact_info')
    .value;
  const tileId = tile.id;
  const isValidQuestion = !!question;
  const isValidContact = !!(
    contactInfo &&
    (validateEmail(contactInfo) || validatePhone(contactInfo))
  );
  const valid = !!(isValidQuestion && isValidContact);

  if (valid) {
    const { target } = e;
    target.style.pointerEvents = 'none';

    if (tileNotSubmittable(tile)) {
      target.classList.add('clicked_right_answer');
      target.nextElementSibling.classList.add('disabled');

      return;
    }

    api({
      method: 'POST',
      headers: { 'Content-Type': 'multipart/form-data' },
      url: '/api/question_answers',
      data: jsonToFormData({
        question_answer: {
          question,
          contact_info: contactInfo,
          tile_id: tileId,
        },
      }),
    })
      .then(() => {
        target.innerText = 'Submitting question';
        target.classList.add('clicked_right_answer');
        target.nextElementSibling.classList.add('disabled');
        tile.submitAnswer(tile.id, 0, null);
      })
      .catch(() => {
        document.getElementById('general_error').classList.remove('hide');
        document.getElementById('general_error').innerText =
          'Something went wrong. Please try again later.';
        target.style.pointerEvents = '';
      });
  } else {
    renderQuestionAnswerError(isValidQuestion, isValidContact);
  }
};

const submitSpouseInvitation = (e, tile) => {
  const email = document.getElementById('dependent_user_invitation_email')
    .value;
  const subject = document.getElementById('dependent_user_invitation_subject')
    .value;
  const body = document.getElementById('dependent_user_invitation_body').value;
  const tileId = tile.id;
  const valid = validateEmail(email);

  if (email && subject && body && valid) {
    const { target } = e;
    target.style.pointerEvents = 'none';

    if (tileNotSubmittable(tile)) {
      target.classList.add('clicked_right_answer');

      return;
    }

    Fetcher.xmlHttpRequest({
      method: 'POST',
      path: '/invitation/dependent_user_invitation',
      params: {
        dependent_user_invitation: { email, subject, body, tileId, json: true },
      },
      success: resp => {
        if (resp.status === 'success') {
          document.getElementById('spouse_form_error').innerText = '';
          target.innerText = 'Sending invitation';
          target.classList.add('clicked_right_answer');
          tile.submitAnswer(tile.id, 0, null);
        } else {
          document.getElementById('spouse_form_error').innerText =
            resp.status === 'already_invited'
              ? "You've already invited a spouse. To resend or cancel that invite, go to Settings > Invite Dependent."
              : 'Something went wrong. Please try again later.';
          target.style.pointerEvents = '';
        }
      },
    });
  } else {
    const error = valid
      ? 'All fields must be filled'
      : 'A valid email address is required';
    document.getElementById('spouse_form_error').innerText = error;
  }
};

const MultipleChoice = ({ tile, subtype }) => {
  const { t } = useTranslation('tileView');
  const [charCount, setCharCount] = useState(600);
  return (
    <div
      className="multiple_choice_group content_sections"
      style={{ padding: '0px 24px' }}
    >
      <MultipleChoiceAnswers key={tile.id} tile={tile} subtype={subtype} />
      {subtype === '' && (
        <div id="question_answer_form_hidden" style={{ display: 'none' }}>
          <label>
            <span>Questions</span>
            <span id="qa_char_count" className="character-counter">
              {t('tileQuiz.characterCount', { charCount })}
            </span>
            <span id="question_error" className="hide"></span>
          </label>
          <textarea
            name="question_answer[question]"
            id="question_answer_question"
            rows="4"
            placeholder={t('tileQuiz.questionAnswers.textAreaPlaceholder')}
            maxLength="600"
            readOnly={tile.origin === 'complete' || tile.freeFormResponse}
            onKeyUp={e => updateCharCount(e, 600, setCharCount)}
            onKeyDown={e =>
              e.target.value.length === 0 && clearQuestionAnswerErrors()
            }
            value={questionAnswerValue(tile)}
          ></textarea>
          <label>
            <span>{t('tileQuiz.questionAnswers.preferredContact')}</span>
            <span id="contact_info_error"></span>
          </label>
          <input
            type="text"
            name="question_answer[contact_info]"
            id="question_answer_contact_info"
            placeholder={t('tileQuiz.questionAnswers.contactPlaceholder')}
            onKeyDown={() =>
              !!document.getElementById('contact_info_error').innerText &&
              clearQuestionAnswerErrors()
            }
          />
          <span id="general_error" className="hide"></span>
          <div className="btn-container">
            <Button
              variant="answerBlue"
              className="tile_button tile-form-btn"
              onClick={e => submitQuestionAnswer(e, tile)}
            >
              {t('tileQuiz.questionAnswers.submitAnswer')}
            </Button>
            <Button
              variant="answerBlue"
              className="tile_button tile-form-btn"
              onClick={revertSelection}
            >
              {t('tileQuiz.questionAnswers.cancel')}
            </Button>
          </div>
        </div>
      )}
      {subtype === 'change_email' && (
        <div id="change_email_form_hidden" style={{ display: 'none' }}>
          <label>{t('tileQuiz.changeEmail.newEmail')}</label>
          <input
            type="text"
            name="change_email[email]"
            id="change_email_email"
            placeholder={t('tileQuiz.changeEmail.emailPlaceholder')}
          />
          <label className="change_email_error err" id="email_error"></label>
          <Button
            variant="answerBlue"
            className="tile_button tile-form-btn"
            onClick={e => {
              submitEmailChange(e, tile);
            }}
          >
            {t('tileQuiz.changeEmail.change')}
          </Button>
          <div>
            <Button
              variant="answerBlue"
              className="no_email_change tile-form-btn"
              onClick={revertSelection}
            >
              {t('tileQuiz.changeEmail.dontChangeEmail')}
            </Button>
          </div>
        </div>
      )}
      {subtype === 'invite_spouse' && (
        <div id="invite_spouse_form_hidden" style={{ display: 'none' }}>
          <label>To</label>
          <input
            type="text"
            name="dependent_user_invitation[email]"
            id="dependent_user_invitation_email"
            placeholder={t('tileQuiz.changeEmail.emailPlaceholder')}
          />
          <label>Subject</label>
          <input
            type="text"
            name="dependent_user_invitation[subject]"
            id="dependent_user_invitation_subject"
            defaultValue={tile.dependentBoardSubject}
          />
          <label>Body</label>
          <textarea
            name="dependent_user_invitation[body]"
            id="dependent_user_invitation_body"
            rows="4"
            defaultValue={tile.dependentBoardBody}
          ></textarea>

          <label
            className="change_email_error err"
            id="spouse_form_error"
          ></label>
          <Button
            variant="answerBlue"
            className="tile_button tile-form-btn"
            onClick={e => {
              submitSpouseInvitation(e, tile);
            }}
          >
            {t('tileQuiz.inviteSpouse.send')}
          </Button>
          <p>
            <Button
              variant="answerBlue"
              className="no_invitation tile-form-btn"
              onClick={revertSelection}
            >
              {t('tileQuiz.inviteSpouse.dontInviteSpouse')}
            </Button>
          </p>
        </div>
      )}
      {tile.allowFreeResponse ? (
        <FreeResponse
          tile={tile}
          submitAction={() => {
            checkAnswerForSubmission(tile.correctAnswerIndex, -1, tile);
          }}
          showXBtn={!tile.freeFormResponse}
          style={{ display: tile.freeFormResponse ? 'block' : 'none' }}
        />
      ) : (
        ''
      )}
    </div>
  );
};

const TileQuiz = ({ tile }) => {
  switch (tile.questionSubtype) {
    case 'free_response':
      return (
        <FreeResponse
          tile={tile}
          submitBtnText={tile.answers[0]}
          submitAction={() => {
            checkAnswerForSubmission(tile.correctAnswerIndex, 0, tile);
          }}
        />
      );
    case 'true_false':
    case 'multiple_choice':
    case 'rsvp_to_event':
    case 'feedback':
      return <MultipleChoice tile={tile} />;
    case 'change_email':
    case 'invite_spouse':
    case '':
      return <MultipleChoice tile={tile} subtype={tile.questionSubtype} />;
    default:
      return (
        <div
          className="multiple_choice_group content_sections"
          style={{ padding: '0px 24px' }}
        >
          <div>
            <TileButtonComponent
              key={`${tile.id}-0`}
              answer={tile.answers[0]}
              correctAnswer={true}
              points={tile.points}
              className={`multiple-choice-answer`}
              onClick={() => {
                checkAnswerForSubmission(0, 0, tile);
              }}
              style={{ margin: '0px' }}
              complete={!!tile.complete}
            />
          </div>
        </div>
      );
  }
};

const TileQuizComponent = ({ tile, tileOrigin, submitAnswer }) => {
  const pointerEvent = (tile, tileOrigin) => {
    const tileOriginComplete = tileOrigin === 'complete';

    if (tileOriginComplete && sharedPlanTile(tile)) {
      return '';
    }

    return tile.complete || tileOriginComplete ? 'none' : '';
  };

  return (
    <div
      id="tile-quiz-section"
      className={`tile_quiz ${
        tile.complete || tileOrigin === 'complete' ? 'complete' : ''
      }`}
      style={{
        background: 'white',
        pointerEvents: pointerEvent(tile, tileOrigin),
      }}
    >
      <Divider />
      <QuizHeader dangerouslySetInnerHTML={htmlSanitizer(tile.question)} />
      <TileQuiz
        tile={{
          ...tile,
          submitAnswer,
          origin: tileOrigin,
        }}
      />
    </div>
  );
};

export default TileQuizComponent;
